/**
 * The patient's medicines and the alarm that rings for them, on every screen.
 *
 * `MedicinesProvider` keeps the tracked medicines and the meal times in one
 * place for the app shell: «أدويتي» edits them, and `MedicineAlarmHost` —
 * mounted by the shell for patients — rings for them wherever the patient is,
 * not only while «أدويتي» is open. Both stay in this browser (localStorage),
 * as before: the backend has no medicine table.
 *
 * The host also reads back the tone the patient uploaded in the settings
 * (GET /api/auth/ringtone), once, and only when a medicine has its alarm on.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useMedicineAlarm } from '../../hooks/useMedicineAlarm';
import { authApi } from '../../api/endpoints';
import { DEFAULT_MEAL_TIMES, MEAL_TIMES_STORAGE_KEY, medicinesStorageKey } from '../../domain/medication';
import { MedicineAlarmModal } from './MedicineAlarmModal';

const MedicinesContext = createContext(null);

export function MedicinesProvider({ children }) {
  const { patientId } = useAuth();
  const [medicines, setMedicines] = useLocalStorage(medicinesStorageKey(patientId), []);
  const [mealTimes, setMealTimes] = useLocalStorage(MEAL_TIMES_STORAGE_KEY, DEFAULT_MEAL_TIMES);

  const value = useMemo(
    () => ({ medicines, setMedicines, mealTimes, setMealTimes }),
    [medicines, setMedicines, mealTimes, setMealTimes],
  );
  return <MedicinesContext.Provider value={value}>{children}</MedicinesContext.Provider>;
}

/** The medicines and meal times, from the shell's `MedicinesProvider`. */
export function useMedicines() {
  const value = useContext(MedicinesContext);
  if (!value) throw new Error('useMedicines must be used inside MedicinesProvider');
  return value;
}

export function MedicineAlarmHost() {
  const { medicines, mealTimes } = useMedicines();
  const armed = medicines.some((medicine) => medicine?.alarmEnabled);
  const [ringtone, setRingtone] = useState(null);

  useEffect(() => {
    if (!armed) return undefined;
    let live = true;
    authApi
      .ringtone()
      .then((result) => {
        if (live) setRingtone(result?.ringtone || null);
      })
      .catch(() => {
        /* the default beep rings instead */
      });
    return () => {
      live = false;
    };
  }, [armed]);

  const alarm = useMedicineAlarm(medicines, { enabled: armed, mealTimes, ringtone });

  return (
    <MedicineAlarmModal
      alarm={alarm.activeAlarm}
      onDismiss={alarm.dismiss}
      onSnooze={alarm.snooze}
      onTaken={alarm.markTaken}
    />
  );
}

export default MedicineAlarmHost;
